import axiosInstance from './axios';
import { HttpBaseApiWrapper, RequestOptions } from './common.interfaces';

export type IFHError<T> = {
  // TODO: Remove this
  response?: {
    data: T;
    status: string | number;
  };
};

type CommonFHError = IFHError<{ message: string }>;

export const handleNonStandardErrors = (e: CommonFHError | Error) => {
  if ('response' in e && e.response?.data?.message) {
    console.error(e.response.data.message);
  } else if ('message' in e) {
    console.error(e.message);
  }
  console.error(e as Error);
};

/**
 * Composes url search string from the params object
 */
export const getURLSearchString = (params: object): string => {
  const u = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      u.append(key, value);
    }
  });

  return decodeURIComponent(u.toString());
};

const FALLBACK_ERROR_MESSAGE = 'There was an error processing your request';

export type IFHResponse<T> = Response & {
  data?: T;
  fileName?: string;
};

export declare class RequestError extends TypeError {
  name: string;
  url: string;
  /**
   * Create an instance of RequestError
   * @param {string} message Error message
   * @param {string} url URL of the request
   */
  constructor(message: RequestError['message'], url: RequestError['url']);
}

/**
 * Any http response status other than [200,299] as a result of HttpBaseApi's get, post, put or delete method is
 * thrown back as an instance of this error class
 */
export declare class HttpError extends Error {
  name: string;
  url: string;
  status: number;
  statusText: string;
  responseData: unknown;
  /**
   * Create an instance of HttpError
   * @param {string} message Error message
   * @param {string} url URL of the response
   * @param {string} status HTTP status code of the response
   * @param {string} statusText Status message corresponding to the HTTP status code
   * @param {unknown} responseData The resolved response of the request that resulted in creating this error
   */
  constructor(
    message: HttpError['message'],
    url: HttpError['url'],
    status: HttpError['status'],
    statusText: HttpError['statusText'],
    responseData: HttpError['responseData']
  );
}

/**
 * Provides an interface that helps standardizing http requests from front-end
 */
export declare class HttpBaseApi {
  /**
   * Make a network request of the specified type.
   * @param {string} method A string to set HTTP method for the request
   * @param {string} url The URL for the request
   * @param {object | null} body An object containing data that you want to apply to the request
   * @param {RequestOptions} options An object containing custom settings that you want to apply to the request
   * @return {Promise} A promise that resolves to the response given by the network request
   */
  private static method;
  /**
   * Make a network request of type 'GET'
   * @param {string} url The URL for the GET request
   * @param {RequestOptions} options An object containing custom settings that you want to apply to the request
   * @return {Promise} A promise that resolves to the response given by the network request
   */
  static get<T>(
    url: string,
    options?: RequestOptions
  ): Promise<HttpBaseApiResponse<T>>;
  /**
   * Make a network request of type 'POST'
   * @param {string} url The URL for the POST request
   * @param {object} body An object containing data that you want to apply to the request
   * @param {RequestOptions} options An object containing custom settings that you want to apply to the request
   * @return {Promise} A promise that resolves to the response given by the network request
   */
  static post<T>(
    url: string,
    body?: object,
    options?: RequestOptions
  ): Promise<HttpBaseApiResponse<T>>;
  /**
   * Make a network request of type 'PUT'
   * @param {string} url The URL for the PUT request
   * @param {object} body An object containing data that you want to apply to the request
   * @param {RequestOptions} options An object containing custom settings that you want to apply to the request
   * @return {Promise} A promise that resolves to the response given by the network request
   */
  static put<T>(
    url: string,
    body?: object,
    options?: RequestOptions
  ): Promise<HttpBaseApiResponse<T>>;
  /**
   * Make a network request of type 'PATCH'
   * @param {string} url The URL for the PATCH request
   * @param {object} body An object containing data that you want to apply to the request
   * @param {RequestOptions} options An object containing custom settings that you want to apply to the request
   * @return {Promise} A promise that resolves to the response given by the network request
   */
  static patch<T>(
    url: string,
    body?: object,
    options?: RequestOptions
  ): Promise<HttpBaseApiResponse<T>>;
  /**
   * Make a network request of type 'DELETE'
   * @param {string} url The URL for the DELETE request
   * @param {object} body An object containing data that you want to apply to the request
   * @param {RequestOptions} options An object containing custom settings that you want to apply to the request
   * @return {Promise} A promise that resolves to the response given by the network request
   */
  static delete<T>(
    url: string,
    body?: object,
    options?: RequestOptions
  ): Promise<HttpBaseApiResponse<T>>;
}

export type HttpBaseApiResponse<T> = {
  /** Response provided by the server */
  data: T;
  /** @deprecated extra info about the response. This property is temporary and will be removed soon */
  _meta?: {
    /** If error thrown when parsing response as json, it will be stored here */
    responseParseError?: unknown;
    /** HTTP status code from the server response */
    status: number;
    /** HTTP status message from the server response */
    statusText: string;
    /** options that was provided to the request */
    options?: RequestOptions;
    /** Headers with which the request was made  */
    requestHeaders: HeadersInit;
    /** Headers that the server responded with  */
    responseHeaders: Headers;
  };
};

export type TransformRequestArgsFn = (
  relativeUrl: string,
  options?: RequestOptions
) => Promise<[string, RequestOptions]>;

export const createHttpBaseApiWrapper = (
  endpoint: string
): HttpBaseApiWrapper => {
  const transformRequestArgs: TransformRequestArgsFn = async (
    relativeUrl,
    options
  ): Promise<[string, RequestOptions]> => {
    const searchParams = options?.params
      ? `?${getURLSearchString(options.params)}`
      : '';
    const url: string = endpoint + relativeUrl + searchParams;
    const token = window.sessionStorage.getItem('api-token') ?? '';
    const requestOptions: RequestOptions = {
      headers: {
        /*eslint no-useless-computed-key: "off"*/
        token,
        ...options?.headers,
      } as any,
    };

    return [url, requestOptions];
  };

  const handleError = (e: any) => {
    console.error(e?.statusText ?? FALLBACK_ERROR_MESSAGE);
    // if (e instanceof RequestError) {
    //   console.error(e);
    // }

    // if (e instanceof HttpError) {
    //   console.error(e.statusText ?? FALLBACK_ERROR_MESSAGE);

    //   if (e.status === 401) {
    //     // TODO: handle reauth here
    //   }
    // }

    throw e;
  };

  /**
   * Add logs to collect data for all apis that are giving error when parsing the response as json
   * This is only temporary to collect information. Once we have figured out all such apis,
   * we need to think of properly handling this.
   */
  const addLogsForApiResponseParseError = (meta: any, url: string) => {
    if (meta?.responseParseError) {
      console.error(
        'HttpBaseApi: Error parsing response as json for url: ',
        url,
        ', requestHeaderAccept: ',
        meta.requestHeaders?.['Accept'], // eslint-disable-line dot-notation
        ', responseHeaderContentType: ',
        meta.responseHeaders.get('Content-Type'),
        ', error: ',
        meta.responseParseError
      );
    }
  };

  const processReponse = (url: string) => {
    return <T>(response: HttpBaseApiResponse<T>) => {
      const { _meta, data } = response;
      addLogsForApiResponseParseError(_meta, url);
      return data;
    };
  };

  return {
    get: async <T>(
      relativeUrl: string,
      options?: RequestOptions
    ): Promise<T> => {
      const [url, requestOptions] = await transformRequestArgs(
        relativeUrl,
        options
      );
      return axiosInstance
        .get<T>(url, requestOptions)
        .then(processReponse(url))
        .catch(handleError);
    },

    post: async <T>(
      relativeUrl: string,
      body?: object,
      options?: RequestOptions
    ): Promise<T> => {
      const [url, requestOptions] = await transformRequestArgs(
        relativeUrl,
        options
      );
      return axiosInstance
        .post<T>(url, body, requestOptions)
        .then(processReponse(url))
        .catch(handleError);
    },

    put: async <T>(
      relativeUrl: string,
      body?: object,
      options?: RequestOptions
    ): Promise<T> => {
      const [url, requestOptions] = await transformRequestArgs(
        relativeUrl,
        options
      );
      return axiosInstance
        .put<T>(url, body, requestOptions)
        .then(processReponse(url))
        .catch(handleError);
    },

    patch: async <T>(
      relativeUrl: string,
      body?: object,
      options?: RequestOptions
    ): Promise<T> => {
      const [url, requestOptions] = await transformRequestArgs(
        relativeUrl,
        options
      );
      return axiosInstance
        .patch<T>(url, body, requestOptions)
        .then(processReponse(url))
        .catch(handleError);
    },

    delete: async <T>(
      relativeUrl: string,
      body?: object,
      options?: RequestOptions
    ): Promise<T> => {
      const [url] = await transformRequestArgs(relativeUrl, options);
      return axiosInstance
        .delete<T>(url, body)
        .then(processReponse(url))
        .catch(handleError);
    },

    // TODO: Extra methods unique to TMS
    // ...createExtraHttpMethods(endpoint, transformRequestArgs, handleError),
  };
};
